import { useState } from 'react';
import { api, errorMessage } from '../lib/api';
import {
  BAND_CHOICES,
  CRITERIA,
  band as fmt,
  taskBandOf,
  writingBandOf,
  type WritingCriteria,
  type WritingTaskMark,
} from '../lib/results';
import { formatDate } from '../lib/when';
import { copyScript, wordCount } from '../lib/scripts';
import { Banner } from './Banner';

interface Props {
  resultId: string;
  /** What the candidate wrote, keyed by task number. */
  essays: Record<number, string>;
  /** When the scripts were cleared, if they have been. */
  scriptsClearedAt: string | null;
  /** How long each task ran to. Kept after the script itself is cleared. */
  scriptWords: Record<number, number> | null;
  existing: WritingTaskMark[] | null;
  onMarked: () => void;
}

const blank = (): WritingCriteria => ({
  taskAchievement: 6,
  coherenceCohesion: 6,
  lexicalResource: 6,
  grammaticalRange: 6,
});

/**
 * Marking a Writing paper.
 *
 * The essay and the criteria sit side by side, because an examiner reads and
 * scores at the same time — putting the scores on another screen would mean
 * scrolling back and forth for every judgement.
 *
 * The band each task implies is shown as the scores change, and the paper band
 * with it, so the examiner sees the consequence of a choice rather than
 * discovering it after saving.
 */
export function WritingMarker({
  resultId,
  essays,
  scriptsClearedAt,
  scriptWords,
  existing,
  onMarked,
}: Props) {
  /*
   * A task with no text is one of two different things, and the screen has to
   * say which: nothing was ever written, or it was written, marked, and
   * cleared two days after the candidate got the result. Only the second has a date on it.
   */
  const cleared = Boolean(scriptsClearedAt);
  /** Which task was last copied, so the button can say so and then stop. */
  const [copied, setCopied] = useState<number | null>(null);

  async function copyEssay(task: number) {
    if (await copyScript(essays[task] ?? '', document.getElementById(`essay-${task}`))) {
      setCopied(task);
      window.setTimeout(() => setCopied((current) => (current === task ? null : current)), 2_000);
    }
  }

  const [marks, setMarks] = useState<Record<1 | 2, WritingCriteria>>(() => ({
    1: existing?.find((m) => m.task === 1)?.criteria ?? blank(),
    2: existing?.find((m) => m.task === 2)?.criteria ?? blank(),
  }));
  const [comments, setComments] = useState<Record<1 | 2, string>>(() => ({
    1: existing?.find((m) => m.task === 1)?.comments ?? '',
    2: existing?.find((m) => m.task === 2)?.comments ?? '',
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bandOne = taskBandOf(marks[1]);
  const bandTwo = taskBandOf(marks[2]);
  const overall = writingBandOf(bandOne, bandTwo);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/results/${resultId}/mark-writing`, {
        tasks: ([1, 2] as const).map((task) => ({
          task,
          criteria: marks[task],
          comments: comments[task].trim() || null,
        })),
      });
      onMarked();
    } catch (err) {
      setError(errorMessage(err, 'Could not save these marks'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      {error && <Banner tone="error">{error}</Banner>}

      <section className="panel">
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div>
            <p className="eyebrow">Band this paper would receive</p>
            <p className="scoreline__band mono">{fmt(overall)}</p>
          </div>
          <p className="muted" style={{ maxWidth: '34ch' }}>
            Task 1 <span className="mono">{fmt(bandOne)}</span>, Task 2{' '}
            <span className="mono">{fmt(bandTwo)}</span>. Task 2 counts double, as in the real exam:
            (Task 1 + 2 × Task 2) ÷ 3.
          </p>
        </div>
        {/*
          * Said before the marking, not after it.
          *
          * The script goes two days after the result reaches the candidate, and the moment
          * to tell a marker that is while they still have it in front of them
          * and a Copy button beside it — not on the day it turns out to be
          * gone.
          */}
        {!cleared && (
          <p className="field__hint" style={{ marginTop: 'var(--s-4)' }}>
            Scripts are cleared two days after the result reaches the candidate — when you mark
            it, or when results are released if your centre releases them by hand — so the centre
            is not holding essays it has finished with. The candidate can copy theirs in that time
            too. The bands, the criteria and your comments are kept. Copy anything you want to
            keep or send on.
          </p>
        )}
      </section>

      {([1, 2] as const).map((task) => (
        <section key={task} className="panel stack">
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
            <h2 className="panel__title" style={{ marginBottom: 0 }}>
              Task {task}
            </h2>
            <div className="row" style={{ gap: 'var(--s-3)', alignItems: 'baseline' }}>
              <p className="muted mono">
                {cleared ? (scriptWords?.[task] ?? 0) : wordCount(essays[task] ?? '')} words · task
                band {fmt(taskBandOf(marks[task]))}
              </p>
              {/*
                * The script itself, on the clipboard.
                *
                * It could always be selected — nothing blocks that — but
                * selecting three hundred words by dragging across a panel that
                * scrolls, without catching the band controls beside it, is a
                * small ordeal for something a marker does for every candidate
                * in a class. The paragraph breaks come with it, so it pastes
                * into a feedback document as the candidate wrote it.
                */}
              {/* Nothing to copy once the script is cleared, so the button goes
                * rather than sitting there greyed out. */}
              {!cleared && (
                <button
                  className="btn btn--ghost"
                  disabled={!essays[task]?.trim()}
                  onClick={() => void copyEssay(task)}
                >
                  {copied === task ? 'Copied' : 'Copy'}
                </button>
              )}
            </div>
          </div>

          <div className="marking">
            <article className="marking__essay" id={`essay-${task}`}>
              {essays[task]?.trim() ? (
                essays[task]!.split(/\n{2,}/).map((para, i) => <p key={i}>{para}</p>)
              ) : cleared ? (
                <p className="muted">
                  Cleared on {formatDate(scriptsClearedAt)}, two days after the result reached the candidate.
                  The band and the four criteria below are the record and stay.
                </p>
              ) : (
                <p className="muted">Nothing was written for this task.</p>
              )}
            </article>

            <div className="marking__criteria">
              {CRITERIA.map((c) => (
                <div key={c.key} className="field">
                  <label className="field__label" htmlFor={`${task}-${c.key}`}>
                    {c.label}
                  </label>
                  <select
                    id={`${task}-${c.key}`}
                    className="field__input mono"
                    value={marks[task][c.key]}
                    onChange={(e) =>
                      setMarks((m) => ({
                        ...m,
                        [task]: { ...m[task], [c.key]: Number(e.target.value) },
                      }))
                    }
                  >
                    {BAND_CHOICES.map((b) => (
                      <option key={b} value={b}>
                        {b.toFixed(1)}
                      </option>
                    ))}
                  </select>
                  <p className="field__hint">{task === 1 ? c.task1 : c.task2}</p>
                </div>
              ))}

              <div className="field">
                <label className="field__label" htmlFor={`${task}-comments`}>
                  Comments for the student
                </label>
                <textarea
                  id={`${task}-comments`}
                  className="field__input register"
                  rows={4}
                  value={comments[task]}
                  onChange={(e) => setComments((c) => ({ ...c, [task]: e.target.value }))}
                  placeholder="What would move this up a band?"
                />
              </div>
            </div>
          </div>
        </section>
      ))}

      <div className="row">
        <button className="btn btn--primary" disabled={busy} onClick={() => void save()}>
          {busy ? 'Saving…' : existing ? 'Update the marks' : `Mark this paper band ${fmt(overall)}`}
        </button>
        <span className="field__hint">
          Both tasks are saved together. The student sees nothing until you release the results.
        </span>
      </div>
    </div>
  );
}
